import classNames from 'classnames'
import { useTranslationTyped } from 'i18n/i18n'
import { useMemo, useState, useEffect } from 'react'

type SpinnerProps = {
  size?: number
  thickness?: number
  className?: string
}

export function Spinner({ size, thickness, className }: SpinnerProps) {
  const circleStyle = useMemo(() => {
    const thicknessOrDefault = 8 * (8 / (thickness || 8))
    let circleStyle
    if (size) {
      circleStyle = {
        width: `${size}rem`,
        height: `${size}rem`,
        borderWidth: `${size / thicknessOrDefault}rem`,
      }
    }
    return circleStyle
  }, [size, thickness])

  return (
    <span
      className={classNames('spinner', className)}
      style={circleStyle}
    ></span>
  )
}

export function DelayedSpinner({
  size,
  thickness,
  className,
  delayMs = 200,
}: SpinnerProps & { delayMs?: number }) {
  const [showSpinner, setShowSpinner] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSpinner(true)
    }, delayMs)

    return () => clearTimeout(timer)
  }, [delayMs])

  if (!showSpinner) {
    return null
  }

  return <Spinner size={size} thickness={thickness} className={className} />
}

export function SpinnerWithLogo({ className }: { className?: string }) {
  const { tt } = useTranslationTyped()
  return (
    <div
      className="relative flex flex-col items-center justify-center"
      aria-live="polite"
      role="status"
    >
      <Spinner size={6} thickness={6} className={className} />
      <img
        src="/assets/images/logo.png"
        width={64}
        className="absolute"
        alt=""
      />
      <span className="sr-only">{tt.common.loading_and_dots()}</span>
    </div>
  )
}

export function FullPageSpinner({ className }: { className?: string }) {
  const { tt } = useTranslationTyped()
  return (
    <div
      className="fixed flex h-screen w-screen flex-col items-center justify-center"
      aria-live="polite"
      role="status"
    >
      <SpinnerWithLogo className={className} />
      <span className="sr-only">{tt.common.loading_and_dots()}</span>
    </div>
  )
}

export function SpinnerCentered(props: SpinnerProps) {
  const { tt } = useTranslationTyped()
  return (
    <div
      className="flex items-center justify-center"
      aria-live="polite"
      role="status"
    >
      <Spinner {...props} />
      <span className="sr-only">{tt.common.loading_and_dots()}</span>
    </div>
  )
}
